import { t } from 'i18next';
import { Dispatch, SetStateAction } from 'react';

import { NotificationPosition } from 'core/enums';
import { Button, Confirmation, Notification, RadioButton, Visible } from 'coreOld/components';
import { Grid } from 'coreOld/elements';
import { Size } from 'coreOld/enums';
import { roundNumber, roundNumberWithUom } from 'coreOld/utils';
import PricingForm from 'partsOld/components/PricingForm';
import { ICompanyPart, IPricing } from 'partsOld/types';
import { PricingType } from 'pricing/enums';

import { ErrorWrapper, Item, TitleWrapper, Wrapper } from './pricingHeaderStyle';
import ManualPriceFields from './ManualPriceFields';

interface IProps {
  part: Partial<ICompanyPart>;
  selectedPrice: IPricing;
  setSelectedPrice: Dispatch<SetStateAction<IPricing>>;
  setIsFormOpen: Dispatch<SetStateAction<boolean>>;
  isFormOpen: boolean;
  setIsConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  isConfirmationOpen: boolean;
  onConfirm: () => Promise<void>;
  onManualCheckbox: (data) => void;
}

const PricingHeaderView = ({
  part,
  isFormOpen,
  setIsFormOpen,
  selectedPrice,
  setSelectedPrice,
  isConfirmationOpen,
  setIsConfirmationOpen,
  onConfirm,
  onManualCheckbox,
}: IProps) => {
  const pricingType = part?.pricingType;

  return (
    <>
      <ErrorWrapper>
        <Notification position={NotificationPosition.TAB} />
      </ErrorWrapper>

      <Wrapper>
        <TitleWrapper>
          <Item bold>{t('labelPricing')}</Item>
          <Button size={Size.XS} onClick={() => setIsFormOpen(true)}>
            {t('buttonLabelAdd')}
          </Button>
        </TitleWrapper>

        <Grid container spacing={1} alignItems={'center'}>
          <Grid item md={3} sm={6} xs={12}>
            <RadioButton
              name={PricingType.CALCULATED}
              label={t('labelCalculatedListPrice')}
              option={PricingType.CALCULATED}
              value={pricingType}
              onChange={() => {
                setIsConfirmationOpen(true);
              }}
            />
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Item bold>
              {part?.pricingType === PricingType.CALCULATED
                ? roundNumberWithUom(
                    part?.originalListPrice,
                    part?.bomPriceDecimals,
                    part?.originalListPriceUom
                  )
                : '-'}
            </Item>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <RadioButton
              name={PricingType.MANUAL}
              label={t('labelManualListPrice')}
              option={PricingType.MANUAL}
              value={pricingType}
              onChange={(opt) => onManualCheckbox(opt)}
            />
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Visible when={pricingType === PricingType.MANUAL}>
              <ManualPriceFields
                part={part}
                priceValue={roundNumber(
                  part?.originalManualListPrice,
                  part?.bomPriceDecimals,
                  false
                )}
                uomValue={part?.originalManualListPriceUom}
                field="listPrice"
              />
            </Visible>

            <Visible when={pricingType === PricingType.CALCULATED}>
              <Item bold>
                {roundNumberWithUom(
                  part?.originalManualListPrice,
                  part?.bomPriceDecimals,
                  part?.originalManualListPriceUom
                )}
              </Item>
            </Visible>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Item marginLeft={20}>{t('labelCalculatedFloorPrice')}</Item>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Item bold>
              {part?.pricingType === PricingType.CALCULATED
                ? roundNumberWithUom(
                    part?.originalFloorPrice,
                    part?.bomPriceDecimals,
                    part?.originalFloorPriceUom
                  )
                : '-'}
            </Item>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Item marginLeft={20}>{t('labelManualFloorPrice')}</Item>
          </Grid>

          <Grid item md={3} sm={6} xs={12}>
            <Visible when={pricingType === PricingType.MANUAL}>
              <ManualPriceFields
                part={part}
                priceValue={roundNumber(
                  part?.originalManualFloorPrice,
                  part?.bomPriceDecimals,
                  false
                )}
                uomValue={part?.originalManualFloorPriceUom}
                field="floorPrice"
              />
            </Visible>

            <Visible when={pricingType === PricingType.CALCULATED}>
              <Item bold>
                {roundNumberWithUom(
                  part?.originalManualFloorPrice,
                  part?.bomPriceDecimals,
                  part?.originalManualFloorPriceUom
                )}
              </Item>
            </Visible>
          </Grid>
        </Grid>

        <Visible when={isConfirmationOpen}>
          <Confirmation
            isOpen={isConfirmationOpen}
            onConfirm={onConfirm}
            onCancel={() => setIsConfirmationOpen(false)}
            title={t('labelInfo')}
            message={t('messageChangePricingToCalculated')}
            size={Size.MD}
          />
        </Visible>

        <PricingForm
          isFormOpen={isFormOpen}
          setIsFormOpen={setIsFormOpen}
          selectedPrice={selectedPrice}
          part={part}
          setSelectedPrice={setSelectedPrice}
        />
      </Wrapper>
    </>
  );
};

export default PricingHeaderView;
